import { useContext, useEffect, useEffectEvent, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Link, useParams, useSearchParams } from 'react-router'
import { Alert, Box, CircularProgress, Grid, Typography } from '@mui/material'

import { AppContext, useHost } from '../../providers/app-provider'
import Api from '../../utils/api'
import useSharedStyles from '../../utils/shared.styles'

import OrderDevicesTable from './devices-table-order'
import ReorderDevicesTable from './devices-table-reorder'
import ReturnDevicesTable from './devices-table-return'
import { getOrderIcon, getOrderTitle } from './helpers'
import useStyles from './index.styles'
import OrderInfo from './info-order'
import ReorderInfo from './info-reorder'
import ReturnInfo from './info-return'
import OrderContainer from './order-container'
import OrderItemList from './order-item-list'
import OrderTrackingTable from './order-tracking-table'

interface IProps {
  folderId: number
  folderName: string
  accountNumber: string
  orderType: OrderType
  orderDetail?: IOrderDetail | IReorderDetail | IReturnDetail
}

const OrderDetails = ({ folderId, folderName, accountNumber, orderType, orderDetail: initialOrderDetail }: IProps) => {
  const classes = useStyles
  const sharedClasses = useSharedStyles

  const auth = useAuth()
  const host = useHost()
  const { tenantId, pageRefreshCounter } = useContext(AppContext)
  const [searchParams] = useSearchParams()
  const { orderId: orderIdParam } = useParams()
  const orderIdSearchParam = searchParams.get('orderId')
  const fetchOrderId = orderIdParam || orderIdSearchParam || initialOrderDetail?.orderNodeId

  const [orderDetail, setOrderDetail] = useState<IOrderDetail | IReorderDetail | IReturnDetail | undefined>(initialOrderDetail)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const [orderRefreshCounter, setOrderRefreshCounter] = useState<number>(0)

  const fetchOrder = useEffectEvent(async () => {
    if (!host || !auth.user?.access_token || (orderDetail && orderDetail?.orderNodeId === fetchOrderId && orderRefreshCounter < 1)) return
    if (fetchOrderId) {
      setLoading(true)
      setError(null)
      const res = await Api.get(
        `${host}/api/v2/central/folders/${folderId}/${orderType === 'return' || orderType === 'scrap' ? 'returns' : orderType === 'reorder' ? 'reorders' : 'orders'}/${fetchOrderId}/details`
      )
      setLoading(false)
      if (res.ok) {
        setOrderDetail(res.body)
      } else {
        setError(`Could not load ${orderType} details`)
      }
    }
  })
  useEffect(() => {
    fetchOrder()
  }, [orderRefreshCounter, fetchOrderId, folderId, orderType, host, pageRefreshCounter])

  if ((!orderDetail && !error) || (loading && !error))
    return (
      <Grid container={true} sx={[sharedClasses.centerContent, { height: '100%', padding: 3 }]}>
        <CircularProgress />
      </Grid>
    )
  if (error || !orderDetail)
    return (
      <Box
        sx={{
          padding: 3,
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    )

  const title = getOrderTitle(orderType)
  const icon = getOrderIcon(orderType)

  return (
    <>
      <Box
        data-testid="order-header"
        sx={{
          padding: 3,
        }}
      >
        <Grid
          container={true}
          size={12}
          sx={{
            gap: 2,
          }}
        >
          <Grid container={true} size="auto" sx={sharedClasses.centerContentVertical}>
            <img src={icon} style={classes.orderIcon} />
          </Grid>
          <Grid size="auto">
            <Typography variant="h6">
              {title} {orderDetail.orderNodeUid}
            </Typography>
            <Typography variant="body1" component="span" className="fs-exclude">
              <Link to={`/tenant/${tenantId}/accounts/${folderId}`}>{folderName}</Link>
            </Typography>
            {accountNumber && (
              <Typography variant="body1" component="span" className="fs-exclude" sx={sharedClasses.secondaryText}>
                {' • '}Acct#: {accountNumber}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
      <Grid sx={{ overflowY: 'auto', paddingX: 3, paddingBottom: 3 }} size={12}>
        <OrderContainer title={`${title} Info`}>
          {['order', 'Battery Replacement Order', 'Replacement Order'].includes(orderType) && (
            <OrderInfo orderDetail={orderDetail as IOrderDetail} setOrderRefreshCounter={setOrderRefreshCounter} />
          )}
          {orderType === 'reorder' && <ReorderInfo orderDetail={orderDetail as IReorderDetail} />}
          {(orderType === 'return' || orderType === 'scrap') && <ReturnInfo orderDetail={orderDetail as IReturnDetail} />}
        </OrderContainer>

        <OrderContainer title="Details">
          <Box>
            {orderType !== 'return' && <OrderItemList orderItems={(orderDetail as IOrderDetail | IReorderDetail).items} />}
            {orderType !== 'reorder' && orderType !== 'return' && <OrderDevicesTable expandedDetails={orderDetail as IOrderDetail} />}
            {orderType === 'reorder' && <ReorderDevicesTable expandedDetails={orderDetail as IReorderDetail} />}
            {(orderType === 'return' || orderType === 'scrap') && <ReturnDevicesTable expandedDetails={orderDetail as IReturnDetail} />}
          </Box>
        </OrderContainer>

        {orderType === 'order' && (
          <OrderContainer title="Tracking Info">
            <OrderTrackingTable timeline={(orderDetail as IOrderDetail).timeline} />
          </OrderContainer>
        )}
      </Grid>
    </>
  )
}

export default OrderDetails
